/**
 * Level-aware menu spec (roadmap Tier 2): build a level-select Screen from the level
 * roster + Progression, so the menu stays in sync with the levels instead of being
 * hand-drawn and drifting. Nodes = levels (label = order · name), each zone's notes =
 * its unlock rule derived from the progression mode. The build agent implements the
 * actual hub from this spec (Bumble's shipped selector is the proof of shape).
 *
 * Pure + deterministic (no Date/random) so it round-trips and unit-tests cleanly.
 */
import type { LevelEntry, Screen, Zone } from '@volley/crucible-ipc-types';
import type { Progression } from '@volley/crucible-levels';
import { progressionOrder } from '../ProgressionEditor';

const TV_W = 1920;
const TV_H = 1080;
// grid geometry (all multiples of 20 to match the editor's snap)
const COLS = 4;
const CW = 400;
const CH = 160;
const GAP = 40;
const X0 = 80;
const Y0 = 240;

function unlockNote(
  progression: Progression | undefined,
  orderedIds: string[],
  id: string,
  nameOf: (id: string) => string,
): string {
  const mode = progression?.mode ?? 'open';
  const entries = progression?.entries ?? [];
  const idx = entries.findIndex((e) => e.levelId === id);
  if (mode === 'open') return 'Always available.';
  if (mode === 'linear') {
    const pos = orderedIds.indexOf(id);
    return pos <= 0 ? 'Start here.' : `Unlocks after ${nameOf(orderedIds[pos - 1])}.`;
  }
  if (mode === 'gated') {
    const reqs = entries[idx]?.unlock?.requires ?? [];
    return reqs.length ? `Requires: ${reqs.map(nameOf).join(', ')}.` : 'Open from the start.';
  }
  return 'Grouped into sets — see Progression.';
}

export function levelSelectScreenFromProgression(
  levels: LevelEntry[],
  progression: Progression | undefined,
  existing?: Screen,
): Screen {
  const byId = new Map(levels.map((l) => [l.id, l]));
  const nameOf = (id: string): string => byId.get(id)?.name ?? id;
  const entries = progression?.entries ?? [];
  // progression order first, then any roster levels not yet in the progression
  const inProg = entries.map((e) => e.levelId).filter((id) => byId.has(id));
  const rest = levels.map((l) => l.id).filter((id) => !inProg.includes(id));
  const orderedIds = [...inProg, ...rest];
  const mode = progression?.mode ?? 'open';

  const zones: Zone[] = [
    {
      id: 'title',
      x: X0,
      y: 60,
      width: TV_W - 2 * X0,
      height: 120,
      label: 'Level Select',
      kind: 'text',
      notes: `Auto-generated from Progression (${mode}). Regenerate to re-sync with the roster.`,
      zIndex: 0,
    },
  ];

  orderedIds.forEach((id, i) => {
    const col = i % COLS;
    const row = Math.floor(i / COLS);
    const ord = progression ? progressionOrder(progression, id) : null;
    zones.push({
      id: `level-${id}`,
      x: X0 + col * (CW + GAP),
      y: Math.min(Y0 + row * (CH + GAP), TV_H - CH - 20),
      width: CW,
      height: CH,
      label: `${ord ? `${ord} · ` : ''}${nameOf(id)}`.slice(0, 80),
      kind: 'button',
      notes: unlockNote(progression, orderedIds, id, nameOf),
      zIndex: i + 1,
    });
  });

  return {
    id: existing?.id ?? 'screen_level_select',
    name: existing?.name ?? 'Level Select',
    surface: 'tv',
    description:
      `Level-select hub, auto-generated from Progression (${mode}) — ${orderedIds.length} levels. ` +
      `Nodes are levels (label = order · name); each node's notes carry its unlock rule. ` +
      `Regenerate after editing Progression or the roster so it stays in sync.`,
    zones,
    linkedSectionIds: existing?.linkedSectionIds ?? [],
    tags: [...new Set([...(existing?.tags ?? []), 'level-select', 'generated'])],
    createdAt: existing?.createdAt ?? '',
    updatedAt: '',
  };
}
